/**
 * Plain-language review/edit of the structured profile.
 *
 * Used by two pages that do genuinely different things with the same form, so
 * the submit label is a prop rather than a constant. It used to be hard-coded
 * to `onboarding.reviewFinish` — "See my first matches" — which on the profile
 * page promised matches and delivered a save, and read as a dead button.
 *
 * `heading`/`body` are props for the same reason (PIVOT-145): they used to be
 * hard-coded to onboarding's "Your profile" / "Review and edit before
 * continuing", which on `/profile` sat directly under that page's own `<h1>My
 * profile</h1>` (a second, near-duplicate title) and promised a "continue"
 * step that page doesn't have. (Named `heading`, not `title`:
 * `scripts/check-i18n-attrs.mjs` flags any JSX `title=` as a literal
 * user-facing string, and this one holds a `TranslationKey`, not text.)
 *
 * The two long-form fields (`summary`, `wants`) use `.grow-field`
 * (`styles/primitives.css`) rather than a fixed `rows` count — a five-line
 * value in a `rows={4}` box used to clip its own last line on a screen whose
 * entire job is "review and correct".
 *
 * **The hard constraints (PIVOT-232) are the second card.** `ConstraintsStep`
 * asks them once, during onboarding; this is the only place a candidate can
 * correct them afterwards, and the only path at all for a profile written
 * before that step existed (re-interviewing those users is explicitly out of
 * scope). They ride the same `PUT /profile` as the fields above — the body is
 * a whole `UserProfile` plus a rubric slot, which is exactly the two objects
 * these five answers are stored in. `constraints` is off for onboarding's own
 * review, whose constraints step asked all five one screen earlier.
 *
 * **The career baseline (PIVOT-367) is the third card.** The job the person
 * actually does, as opposed to the job they want. It is here and nowhere else
 * because this is the screen the contract names as the place the baseline is
 * confirmed, and because a card next to "what you're after" is where the
 * difference between the two is easiest to see.
 *
 * Nothing on this form derives it. The card shows what the server proposed
 * from approved evidence, with its review state in words, and posts a
 * correction only when the person used one of the two buttons. Every other
 * save leaves the stored baseline -- version and identity included --
 * untouched, which is what stops an edited summary from reading as a career
 * change.
 */

import { useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import { CONTRACT_TYPES, PROVINCES, constraintState, splitLocations } from '../lib/constraints'
import type {
  CandidateEvidence,
  CareerBaseline,
  CareerBaselineCorrection,
  OnboardingIntent,
  Rubric,
  UserProfile,
} from '../types/domain'
import { UNKNOWN_BASELINE_VALUE } from '../types/domain'
import { Icon } from './Icon'
import './ProfileReview.css'
import '../styles/location-chips.css'

interface ProfileReviewProps {
  profile: UserProfile
  /**
   * Puts the hard-constraint fields on the form (PIVOT-232) — where the
   * candidate will work, from where, for how little, on what contract, in
   * which languages. `/profile` sets it; onboarding's review does not.
   */
  constraints?: boolean
  /**
   * The stored rubric. Only read when `constraints` is set, and only for the
   * commute anchor, which lives on it (`commute.anchor`) rather than on the
   * profile: `passes_region` reads that anchor BESIDE `profile.locations`, so
   * a screen that edited only one of the two would leave the region gate
   * half-blind. `null` is a candidate with no rubric yet — the other four
   * questions still edit fine, and the anchor field is left out rather than
   * collecting an answer with nowhere to store it.
   */
  rubric?: Rubric | null
  /**
   * The stored career baseline (PIVOT-367). `undefined` leaves the card out
   * entirely -- a caller that cannot supply one must not render an empty
   * version of it. `null` renders the card in its "nothing on file" state,
   * which is a real answer for an account created before this ticket.
   */
  careerBaseline?: CareerBaseline | null
  /**
   * The person's approved evidence, read ONLY to list what a correction may
   * point at. A stated occupation needs a source the account actually holds
   * (the server refuses one without), so the correction offers a choice of
   * stored entries instead of a bare text box.
   */
  candidateEvidence?: CandidateEvidence | null
  /**
   * Saves the edits. The second argument is the rubric to save with them,
   * present only when this form actually edits one (see `rubric`); the caller
   * passes it to `PUT /profile` untouched. The third is a baseline
   * confirmation or correction, present ONLY when the person used one of the
   * baseline buttons -- an ordinary save omits it and the stored baseline
   * does not move.
   */
  onSave: (
    profile: UserProfile,
    rubric?: Rubric,
    careerBaseline?: CareerBaselineCorrection,
  ) => void | Promise<void>
  /** Names what this page's submit actually does. See the module docstring. */
  submitLabel: TranslationKey
  /** Names what this screen is for. See the module docstring. */
  heading: TranslationKey
  body: TranslationKey
  /** Rendered next to the button, so the outcome is where the click was. */
  feedback?: ReactNode
}

/** The salary floor as an integer, or `null` for a box left empty. */
function parseSalary(text: string): number | null {
  const value = Number.parseInt(text, 10)
  return Number.isNaN(value) || value < 0 ? null : value
}

/** One stored entry a baseline correction is allowed to rest on. */
interface BaselineSource {
  evidenceId: string
  label: string
}

/**
 * What the person may point a corrected job at (PIVOT-367).
 *
 * Their own approved biography, minus the qualifications: a diploma proves a
 * diploma, and the server refuses a job title whose only source is one, so
 * offering it here would only produce a rejected save. Sorted by nothing --
 * the evidence arrives in the order it was approved, and reordering it would
 * imply a recency the list does not carry.
 */
function baselineSources(evidence: CandidateEvidence | null | undefined): BaselineSource[] {
  if (!evidence) return []
  const sources: BaselineSource[] = []
  for (const fact of evidence.biography_facts) {
    if (fact.kind === 'qualification') continue
    if (typeof fact.value !== 'string') continue
    const [evidenceId] = fact.evidence_ids
    if (!evidenceId || sources.some((source) => source.evidenceId === evidenceId)) continue
    sources.push({ evidenceId, label: fact.value })
  }
  return sources
}

/** A comma list as the person typed it, with the blanks dropped. */
function parseList(text: string): string[] {
  return text
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
}

/** The stored value, or the words for "we do not know this yet". */
function shownValue(value: string, unknownLabel: string): string {
  return value === UNKNOWN_BASELINE_VALUE ? unknownLabel : value
}

export function ProfileReview({
  profile,
  constraints = false,
  rubric,
  careerBaseline,
  candidateEvidence,
  onSave,
  submitLabel,
  heading,
  body,
  feedback,
}: ProfileReviewProps) {
  const { t } = useI18n()
  const [draft, setDraft] = useState<UserProfile>(profile)
  const [saving, setSaving] = useState(false)
  // The no-constraint boxes, seeded from what the profile already records.
  // They are their own state because they are the *other* kind of answer:
  // "anywhere is fine" is a recorded fact, and an empty box beside an empty
  // field is not it (that is the `locations=[]` ambiguity PIVOT-232 closes).
  const stored = profile.constraint_capture
  const [marks, setMarks] = useState({
    locations: stored.locations === 'no_constraint',
    anchor: stored.commute_anchor === 'no_constraint',
    contracts: stored.contract_types === 'no_constraint',
    salary: stored.salary === 'no_constraint',
    languages: stored.languages === 'no_constraint',
  })
  // Both held as typed text, not as their parsed value: a number input that
  // reparses on every keystroke eats the half-typed "3" of "3000", and the
  // anchor's own home is the rubric, not `draft`.
  const [anchor, setAnchor] = useState(rubric?.commute.anchor ?? '')
  const [salary, setSalary] = useState(
    profile.salary_min === null ? '' : String(profile.salary_min),
  )

  // PIVOT-367. `'none'` is the default and the important one: a save that
  // leaves it there sends no correction at all, so the stored baseline and
  // its identity survive every ordinary edit on this form (A1).
  const [baselineAction, setBaselineAction] = useState<'none' | 'confirm' | 'correct'>('none')
  const sources = baselineSources(candidateEvidence)
  const [baselineRole, setBaselineRole] = useState(
    careerBaseline && careerBaseline.role_title !== UNKNOWN_BASELINE_VALUE
      ? careerBaseline.role_title
      : '',
  )
  const [baselineSourceId, setBaselineSourceId] = useState(
    careerBaseline?.evidence_ids.find((id) => sources.some((s) => s.evidenceId === id)) ?? '',
  )
  const [baselineDuties, setBaselineDuties] = useState(careerBaseline?.main_duties.join(', ') ?? '')
  const [baselineSectors, setBaselineSectors] = useState(
    careerBaseline?.known_employer_sectors.join(', ') ?? '',
  )
  const [baselineMixed, setBaselineMixed] = useState(careerBaseline?.mixed_career ?? false)

  /**
   * The baseline half of the save, or `undefined` for "this edit says nothing
   * about my career".
   *
   * A confirmation re-sends the displayed baseline verbatim, which the server
   * reads as agreement and leaves the version alone. A correction sends what
   * the person typed. Both carry an evidence reference, because a job title
   * with no source on file is exactly what neither side will store.
   */
  const editedBaseline = (): CareerBaselineCorrection | undefined => {
    if (baselineAction === 'none' || careerBaseline === undefined) return undefined
    if (baselineAction === 'confirm' && careerBaseline) {
      return {
        role_title: careerBaseline.role_title,
        occupation_description: careerBaseline.occupation_description,
        main_duties: careerBaseline.main_duties,
        known_employer_sectors: careerBaseline.known_employer_sectors,
        organization_context: careerBaseline.organization_context,
        mixed_career: careerBaseline.mixed_career,
        evidence_ids: careerBaseline.evidence_ids,
      }
    }
    const role = baselineMixed ? UNKNOWN_BASELINE_VALUE : baselineRole.trim()
    return {
      role_title: role || UNKNOWN_BASELINE_VALUE,
      main_duties: parseList(baselineDuties),
      known_employer_sectors: parseList(baselineSectors),
      mixed_career: baselineMixed,
      // A mixed career states that no single job applies, so it needs no
      // source. Anything else does.
      evidence_ids: baselineMixed || !baselineSourceId ? [] : [baselineSourceId],
    }
  }

  // Plain-language labels for the raw enum (constitution: no jargon in front of the user).
  const intentLabels: Record<OnboardingIntent, string> = {
    pivot_new_sector: t('onboarding.intentPivotNewSector'),
    same_sector_new_role: t('onboarding.intentSameSectorNewRole'),
    know_direction: t('onboarding.intentKnowDirection'),
    unsure: t('onboarding.intentUnsure'),
  }

  /**
   * The two objects `PUT /profile` takes, with the constraint answers folded in.
   *
   * A ticked no-constraint box clears its value, so a value and its marker can
   * never both be sent — the same pairing `ConstraintAnswers` enforces on the
   * onboarding side. `constraintState` decides what each field's recorded
   * state becomes; a question this candidate neither answered nor waived keeps
   * whatever the profile already said, which is what leaves a profile written
   * before PIVOT-232 at `never_asked` when someone edits only their summary.
   */
  const edited = (): [UserProfile, Rubric | undefined] => {
    if (!constraints) return [draft, undefined]
    const locations = marks.locations ? [] : draft.locations
    const contractTypes = marks.contracts ? [] : draft.contract_types
    const languages = marks.languages ? [] : draft.languages
    const salaryMin = marks.salary ? null : parseSalary(salary)
    const anchorValue = marks.anchor ? '' : anchor.trim()
    const next: UserProfile = {
      ...draft,
      locations,
      contract_types: contractTypes,
      languages,
      salary_min: salaryMin,
      constraint_capture: {
        locations: constraintState(locations.length > 0, marks.locations, stored.locations),
        // No rubric on this screen means no anchor field was shown, so this
        // candidate answered nothing about it either way.
        commute_anchor: rubric
          ? constraintState(anchorValue.length > 0, marks.anchor, stored.commute_anchor)
          : stored.commute_anchor,
        contract_types: constraintState(
          contractTypes.length > 0,
          marks.contracts,
          stored.contract_types,
        ),
        salary: constraintState(salaryMin !== null, marks.salary, stored.salary),
        languages: constraintState(languages.length > 0, marks.languages, stored.languages),
      },
    }
    if (!rubric) return [next, undefined]
    return [next, { ...rubric, commute: { ...rubric.commute, anchor: anchorValue } }]
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSaving(true)
    try {
      const [nextProfile, nextRubric] = edited()
      await onSave(nextProfile, nextRubric, editedBaseline())
    } finally {
      setSaving(false)
    }
  }

  /** True when this contract is on the list, whatever case it was stored in. */
  const takes = (contract: string) =>
    draft.contract_types.some((value) => value.trim().toLowerCase() === contract)

  /** Toggling one contract leaves every other stored value alone — including a
   * value this form has no box for, which the profile builder may have written
   * and which a rebuilt list would silently drop. */
  const toggleContract = (contract: string, checked: boolean) =>
    setDraft({
      ...draft,
      contract_types: checked
        ? [...draft.contract_types, contract]
        : draft.contract_types.filter((value) => value.trim().toLowerCase() !== contract),
    })

  // A4: `draft.locations` mixes a candidate's ticked province codes with any
  // free-text town/region strings a profile answered before this ticket
  // still carries -- split purely for rendering, same list either way.
  const { provinceCodes: locationCodes, legacy: legacyLocations } = splitLocations(draft.locations)

  const toggleProvince = (code: string, checked: boolean) =>
    setDraft({
      ...draft,
      locations: checked
        ? [...draft.locations, code]
        : draft.locations.filter((value) => value !== code),
    })

  /** The legacy chip's only control: it can be removed, never retyped. */
  const removeLegacyLocation = (location: string) =>
    setDraft({
      ...draft,
      locations: draft.locations.filter((value) => value !== location),
    })

  return (
    <form className="stack" onSubmit={handleSubmit}>
      <h2>{t(heading)}</h2>
      <p>{t(body)}</p>

      {/* PIVOT-143: the `.cv-upload-saved` treatment (surface, accent left
          rule) plus an eyebrow naming the source — this was a bare form with
          nothing distinguishing it from a blank one the candidate is filling
          in themselves, when every field here was actually generated *for*
          them. Shared with `pages/Profile.tsx`'s copy of this component (see
          the module docstring), so this reads correctly there too: it is
          still true on a later visit that the fields came from the CV and
          interview, not from typing. */}
      <div className="profile-review-fields">
        <p className="eyebrow">{t('onboarding.reviewSourceEyebrow')}</p>

        <label className="profile-review-field">
          <span>{t('onboarding.reviewSummaryLabel')}</span>
          {/* Auto-grows to fit the value instead of clipping a long summary
              mid-line (PIVOT-145) — see `.grow-field`'s docstring. */}
          <div className="grow-field" data-replicated-value={draft.summary}>
            <textarea
              value={draft.summary}
              rows={1}
              onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
            />
          </div>
        </label>

        {/* `skills` and `intent` are the only two short-enough controls on this
            form — `summary` and `wants` are long-form and stay full width
            (PIVOT-125). They are not a related pair the way the `.form-row`
            primitive's own example (location + radius) is: pairing them here is
            purely about not wasting the row on two single-line controls, not a
            claim that what you can do and where you want to go are the same
            question. Each keeps its own label directly above it, so the
            side-by-side placement doesn't imply a link that isn't there. */}
        <div className="form-row">
          <label className="profile-review-field">
            <span>{t('onboarding.reviewSkillsLabel')}</span>
            <input
              type="text"
              value={draft.skills.join(', ')}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  skills: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>

          <label className="profile-review-field">
            <span>{t('onboarding.reviewIntentLabel')}</span>
            <div className="select-shell">
              <select
                value={draft.intent}
                onChange={(e) => setDraft({ ...draft, intent: e.target.value as OnboardingIntent })}
              >
                {(Object.keys(intentLabels) as OnboardingIntent[]).map((intent) => (
                  <option key={intent} value={intent}>
                    {intentLabels[intent]}
                  </option>
                ))}
              </select>
              <Icon name="chevron" />
            </div>
          </label>
        </div>

        <label className="profile-review-field">
          <span>{t('onboarding.reviewWantsLabel')}</span>
          <div className="grow-field" data-replicated-value={draft.wants}>
            <textarea
              value={draft.wants}
              rows={1}
              onChange={(e) => setDraft({ ...draft, wants: e.target.value })}
            />
          </div>
        </label>
      </div>

      {/* PIVOT-232: the hard constraints, in their own card because they are a
          different kind of fact from the four above — those were written *for*
          the candidate from the CV and interview, these are the candidate's own
          lines, and they are what the matching gates read. Same questions and
          same words as `ConstraintsStep`, so a correction here reads as the
          answer being changed rather than as a second, differently-worded
          interrogation. */}
      {constraints && (
        <div className="profile-review-fields">
          <p className="eyebrow">{t('onboarding.constraintsOwnEyebrow')}</p>
          <p className="profile-review-lede">{t('onboarding.constraintsBody')}</p>

          <fieldset className="profile-review-constraint">
            <legend className="profile-review-constraint-label">
              {t('onboarding.constraintsLocationsLabel')}
            </legend>
            <div className="profile-review-choices">
              {PROVINCES.map((province) => (
                <label key={province.value} className="profile-review-choice">
                  <input
                    type="checkbox"
                    checked={locationCodes.includes(province.value)}
                    disabled={marks.locations}
                    onChange={(e) => toggleProvince(province.value, e.target.checked)}
                  />
                  {t(province.labelKey)}
                </label>
              ))}
            </div>
            {/* A4: a legacy free-text value from a profile answered before
                this ticket -- removable only, since there is no control left
                that writes a new one of these. */}
            {legacyLocations.length > 0 && (
              <ul className="location-chip-list">
                {legacyLocations.map((location) => (
                  <li key={location} className="location-chip">
                    {location}
                    <button
                      type="button"
                      className="location-chip-remove"
                      disabled={marks.locations}
                      aria-label={t('onboarding.locationChipRemove', { location })}
                      onClick={() => removeLegacyLocation(location)}
                    >
                      <Icon name="cross" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="profile-review-hint">{t('onboarding.constraintsLocationsHint')}</p>
            <label className="profile-review-optout">
              <input
                type="checkbox"
                checked={marks.locations}
                onChange={(e) => setMarks({ ...marks, locations: e.target.checked })}
              />
              {t('onboarding.constraintsLocationsAnywhere')}
            </label>
          </fieldset>

          {/* Only with a rubric to write it to: the anchor lives on
              `rubric.commute`, and `passes_region` reads it beside
              `profile.locations` — see the `rubric` prop. */}
          {rubric && (
            <div className="profile-review-constraint">
              <label htmlFor="profile-review-anchor" className="profile-review-constraint-label">
                {t('onboarding.constraintsAnchorLabel')}
              </label>
              <input
                id="profile-review-anchor"
                type="text"
                value={anchor}
                disabled={marks.anchor}
                onChange={(e) => setAnchor(e.target.value)}
              />
              <p className="profile-review-hint">{t('onboarding.constraintsAnchorHint')}</p>
              <label className="profile-review-optout">
                <input
                  type="checkbox"
                  checked={marks.anchor}
                  onChange={(e) => setMarks({ ...marks, anchor: e.target.checked })}
                />
                {t('onboarding.constraintsAnchorNone')}
              </label>
            </div>
          )}

          <fieldset className="profile-review-constraint">
            <legend className="profile-review-constraint-label">
              {t('onboarding.constraintsContractLabel')}
            </legend>
            <div className="profile-review-choices">
              {CONTRACT_TYPES.map((contract) => (
                <label key={contract.value} className="profile-review-choice">
                  <input
                    type="checkbox"
                    checked={takes(contract.value)}
                    disabled={marks.contracts}
                    onChange={(e) => toggleContract(contract.value, e.target.checked)}
                  />
                  {t(contract.labelKey)}
                </label>
              ))}
            </div>
            <label className="profile-review-optout">
              <input
                type="checkbox"
                checked={marks.contracts}
                onChange={(e) => setMarks({ ...marks, contracts: e.target.checked })}
              />
              {t('onboarding.constraintsContractAny')}
            </label>
          </fieldset>

          <div className="profile-review-constraint">
            <label htmlFor="profile-review-salary" className="profile-review-constraint-label">
              {t('onboarding.constraintsSalaryLabel')}
            </label>
            <input
              id="profile-review-salary"
              type="number"
              min={0}
              step={100}
              value={salary}
              disabled={marks.salary}
              onChange={(e) => setSalary(e.target.value)}
            />
            <p className="profile-review-hint">{t('onboarding.constraintsSalaryHint')}</p>
            <label className="profile-review-optout">
              <input
                type="checkbox"
                checked={marks.salary}
                onChange={(e) => setMarks({ ...marks, salary: e.target.checked })}
              />
              {t('onboarding.constraintsSalaryNoFloor')}
            </label>
          </div>

          <div className="profile-review-constraint">
            <label htmlFor="profile-review-languages" className="profile-review-constraint-label">
              {t('onboarding.constraintsLanguagesLabel')}
            </label>
            {/* Free text, not the step's three level pickers: a CV may name a
                language this form has no row for (German, Spanish), and a
                rebuilt list would delete it on the next save. The level in
                brackets is the part `passes_language` reads, so the hint spells
                the shape out. */}
            <input
              id="profile-review-languages"
              type="text"
              value={draft.languages.join(', ')}
              disabled={marks.languages}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  languages: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
            <p className="profile-review-hint">{t('onboarding.constraintsLanguagesFormatHint')}</p>
            <label className="profile-review-optout">
              <input
                type="checkbox"
                checked={marks.languages}
                onChange={(e) => setMarks({ ...marks, languages: e.target.checked })}
              />
              {t('onboarding.constraintsLanguagesNone')}
            </label>
          </div>
        </div>
      )}

      {/* PIVOT-367: the career baseline. Its own card, between what the CV
          said and what the candidate demands, because it is the fact that
          decides whether a match is a career change at all. The review state
          is written out in words above the values (constitution SI): a
          proposal nobody has confirmed must never read like an answer. */}
      {careerBaseline !== undefined && (
        <div className="profile-review-fields">
          <p className="eyebrow">{t('onboarding.baselineEyebrow')}</p>
          <p className="profile-review-lede">{t('onboarding.baselineBody')}</p>
          <output className="profile-review-baseline-state">
            {t(
              careerBaseline === null
                ? 'onboarding.baselineStateMissing'
                : careerBaseline.review_state === 'baseline_confirmed'
                  ? 'onboarding.baselineStateConfirmed'
                  : careerBaseline.review_state === 'baseline_unknown'
                    ? 'onboarding.baselineStateUnknown'
                    : 'onboarding.baselineStateUnconfirmed',
            )}
          </output>

          {careerBaseline && baselineAction !== 'correct' && (
            <dl className="profile-review-baseline">
              <div>
                <dt>{t('onboarding.baselineRoleLabel')}</dt>
                <dd>
                  {shownValue(careerBaseline.role_title, t('onboarding.baselineUnknownValue'))}
                </dd>
              </div>
              <div>
                <dt>{t('onboarding.baselineDutiesLabel')}</dt>
                <dd>
                  {careerBaseline.main_duties.length > 0
                    ? careerBaseline.main_duties.join(', ')
                    : t('onboarding.baselineUnknownValue')}
                </dd>
              </div>
              <div>
                <dt>{t('onboarding.baselineSectorsLabel')}</dt>
                <dd>
                  {careerBaseline.known_employer_sectors.length > 0
                    ? careerBaseline.known_employer_sectors.join(', ')
                    : t('onboarding.baselineUnknownValue')}
                </dd>
              </div>
            </dl>
          )}

          {baselineAction === 'correct' ? (
            <>
              <label className="profile-review-field">
                <span>{t('onboarding.baselineRoleTitleLabel')}</span>
                <input
                  type="text"
                  value={baselineRole}
                  disabled={baselineMixed}
                  onChange={(e) => setBaselineRole(e.target.value)}
                />
              </label>

              <fieldset className="profile-review-constraint">
                <legend className="profile-review-constraint-label">
                  {t('onboarding.baselineSourceLabel')}
                </legend>
                <p className="profile-review-hint">{t('onboarding.baselineSourceHint')}</p>
                {sources.length === 0 ? (
                  <p className="profile-review-hint">{t('onboarding.baselineNoSources')}</p>
                ) : (
                  <div className="profile-review-choices">
                    {sources.map((source) => (
                      <label key={source.evidenceId} className="profile-review-choice">
                        <input
                          type="radio"
                          name="profile-review-baseline-source"
                          value={source.evidenceId}
                          checked={baselineSourceId === source.evidenceId}
                          disabled={baselineMixed}
                          onChange={() => setBaselineSourceId(source.evidenceId)}
                        />
                        {source.label}
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>

              {/* The hint sits OUTSIDE the label: a second element inside one
                  would join the accessible name, and the field would answer to
                  "What the job involves Separate each one with a comma." */}
              <div className="profile-review-constraint">
                <label className="profile-review-field">
                  <span>{t('onboarding.baselineDutiesLabel')}</span>
                  <input
                    type="text"
                    value={baselineDuties}
                    onChange={(e) => setBaselineDuties(e.target.value)}
                  />
                </label>
                <p className="profile-review-hint">{t('onboarding.baselineListHint')}</p>
              </div>

              {/* The hint sits OUTSIDE the label: a second element inside one
                  would join the accessible name, and the field would answer to
                  "What the job involves Separate each one with a comma." */}
              <div className="profile-review-constraint">
                <label className="profile-review-field">
                  <span>{t('onboarding.baselineSectorsLabel')}</span>
                  <input
                    type="text"
                    value={baselineSectors}
                    onChange={(e) => setBaselineSectors(e.target.value)}
                  />
                </label>
                <p className="profile-review-hint">{t('onboarding.baselineListHint')}</p>
              </div>

              {/* A stated answer, not a blank one: "my career is mixed" is
                  something the person says about themselves, and it needs no
                  source because it names no single job. */}
              <label className="profile-review-optout">
                <input
                  type="checkbox"
                  checked={baselineMixed}
                  onChange={(e) => setBaselineMixed(e.target.checked)}
                />
                {t('onboarding.baselineMixedCareer')}
              </label>

              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setBaselineAction('none')}
              >
                {t('onboarding.baselineKeep')}
              </button>
            </>
          ) : (
            <div className="profile-review-baseline-actions">
              {careerBaseline && (
                <button
                  type="button"
                  className={baselineAction === 'confirm' ? 'btn btn-primary' : 'btn btn-outline'}
                  aria-pressed={baselineAction === 'confirm'}
                  onClick={() => setBaselineAction('confirm')}
                >
                  {t('onboarding.baselineConfirm')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setBaselineAction('correct')}
              >
                {t('onboarding.baselineCorrect')}
              </button>
            </div>
          )}
        </div>
      )}

      <button type="submit" className="btn btn-primary" disabled={saving}>
        {saving ? t('common.loading') : t(submitLabel)}
      </button>
      {feedback}
    </form>
  )
}
